import React, { useMemo, useCallback } from 'react';
import { useRole } from '../../context/roleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import DataTable from '../common/DataTable';
import { useTranslation } from 'react-i18next';

// Number of rows to show per DataTable page
const ITEMS_PER_PAGE = 25;

/**
 * Format GST rate as percentage
 * @param {object} category - Category object from API response
 * @returns {string}
 */


/**
 * ProductCategoryTable Component
 * Displays product categories in a data table with name and GST rate columns
 */
const ProductCategoryTable = ({
  categories = [],
  loading = false,
  error = null,
  selectedCategories = [],
  onSelectCategory,
  onSelectAll,
  onEdit,
  onDelete,
  onView,
  viewLoading = false,
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess, checkEditAccess } = useRole();
  const hasCategories = Array.isArray(categories) && categories.length > 0;
  const selectedCategoriesSet = useMemo(() => new Set(selectedCategories), [selectedCategories]);
  const allSelected = hasCategories && selectedCategories.length === categories.length;

  const hoverColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
    []
  );
  const selectedColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1),
    []
  );
  const selectionAccent = UI_COLORS.BUTTONS.PRIMARY.background;

  /**
   * Table columns configuration
   */
  const columns = useMemo(
    () => [
      {
        id: 'selection',
        label: t('productCategories.table.columns.select'),
        width: '60px',
        align: 'center',
        alignItems: 'center',
        justify: 'center',
        sortable: false,
        renderHeader: ({ loading: isLoading }) => (
          <input
            type="checkbox"
            aria-label={t('productCategories.table.aria.selectAll')}
            checked={allSelected}
            onChange={() => onSelectAll?.(!allSelected)}
            disabled={!hasCategories || isLoading}
            className="rounded focus:ring-2"
            style={{ accentColor: selectionAccent }}
          />
        ),
        renderCell: (category) => {
          const categoryName = category.name || t('productCategories.table.copy.unnamedCategory');
          const ariaLabel = `${t('productCategories.table.aria.selectCategory')} ${categoryName}`;

          return (
            <input
              type="checkbox"
              aria-label={ariaLabel}
              checked={selectedCategoriesSet.has(category.id)}
              onChange={(event) => onSelectCategory?.(category.id, event.target.checked)}
              className="rounded focus:ring-2"
              style={{ accentColor: selectionAccent }}
            />
          );
        },
        skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'name',
        label: t('productCategories.form.name'),
        sortable: true,
        sortAccessor: (category) => (category?.name || '').toLowerCase(),
        renderCell: (category) => (
          <div className="flex items-center space-x-3">
            <div>
              <div className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {category?.name || t('productCategories.table.copy.unnamedCategory')}
              </div>
            </div>
          </div>
        ),
        skeleton: (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-300 rounded-full" />
            <div className="w-32 h-4 bg-gray-300 rounded" />
          </div>
        ),
      },
      {
        id: 'actions',
        label: t('common.table.actions'),
        width: '120px',
        align: 'center',
        alignItems: 'center',
        justify: 'center',
        sortable: false,
        renderCell: (category) => {
          const categoryName = category?.name || t('productCategories.table.copy.unnamedCategory');
          return (
            <div className="flex items-center justify-center space-x-2">

              {checkEditAccess(category.created_at || category.date, { isMasterData: true }) && (
                <button
                  onClick={() => onEdit?.(category)}
                  aria-label={`${t('productCategories.table.aria.editCategory')} ${categoryName}`}
                  className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                  style={{
                    color: UI_COLORS.STATUS.WARNING,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1),
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.WARNING,
                      0.2
                    );
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.WARNING,
                      0.1
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faEdit} className="text-sm" />
                </button>
              )}

              {checkDeleteAccess() && (
                <button
                  onClick={() => onDelete?.(category)}
                  aria-label={`${t('productCategories.table.aria.deleteCategory')} ${categoryName}`}
                  className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                  style={{
                    color: UI_COLORS.STATUS.ERROR,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.ERROR,
                      0.2
                    );
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.ERROR,
                      0.1
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} className="text-sm" />
                </button>
              )}
            </div>
          );
        },
        skeleton: (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-6 h-6 bg-gray-300 rounded" />
            <div className="w-6 h-6 bg-gray-300 rounded" />
            <div className="w-6 h-6 bg-gray-300 rounded" />
          </div>
        ),
      },
    ],
    [
      allSelected,
      hasCategories,
      onSelectAll,
      selectedCategoriesSet,
      onSelectCategory,
      selectionAccent,
      checkDeleteAccess,
      checkEditAccess,
      onEdit,
      onDelete,
      t,
    ]
  );

  const getRowStyle = useCallback(
    (category, isSelected) => ({
      backgroundColor: isSelected ? selectedColor : 'transparent',
      borderLeftColor: isSelected ? selectionAccent : 'transparent',
      borderLeftWidth: isSelected ? '3px' : '0px',
    }),
    [selectedColor, selectionAccent]
  );

  const getRowHoverStyle = useCallback(() => ({
    backgroundColor: hoverColor,
  }), [hoverColor]);

  return (
    <DataTable
      data={categories}
      columns={columns}
      loading={loading}
      emptyStateTitle={t('productCategories.table.emptyState.title')}
      emptyStateMessage={t('productCategories.table.emptyState.message')}
      itemsPerPage={ITEMS_PER_PAGE}
      selectedItems={selectedCategories}
      getRowStyle={getRowStyle}
      getRowHoverStyle={getRowHoverStyle}
      keyExtractor={(category) => `category-${category.id}`}
      enablePagination={false}
    />
  );
};

export default ProductCategoryTable;